import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppTokenModel, DynamicObjectInterface } from '../../../sdk/type';
import {
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  ButtonIconPosition,
  ButtonStyles,
  Button,
  ButtonSize,
  DropDown,
  Modal,
  ButtonTypes,
  TextAlign,
  Checkbox,
  InputValidations,
  useBreakpoint,
  FAIcon,
  IconStyle,
  ButtonWidth,
  RadioButton,
  Tooltip,
  TooltipPreference,
} from '../../../ui/components';
import ShareDrawer from '../../../ui/components/DrawerAlt/ShareDrawer';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../hooks';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { isValidEmail } from '../../../ui/utils/common';
import { getTranslatedMessage, LoggerService, PAGES, trackPage } from '../../../ui/utils';
import TeamService from '../../../sdk/services/teamService';
import userService from '../../../sdk/services/userService';
import CampaignService from '../../../sdk/services/campaignService';
import { getContent } from '../../../sdk/services/contentService';
import ParticipantService from '../../../sdk/services/participantService';
import CreateTeamImage from '../../../assets/image/operationCard/Create_Team.png';
import JoinTeamImage from '../../../assets/image/operationCard/Join_Team.png';
import styles from './index.module.scss';

interface CreateTeamData {
  teamId: number | null;
  teamName: string;
  description?: string;
  campaignId: string;
  campaignName: string;
  campaignRecId?: number;
  teamLeader?: string;
  coachId?: string;
  teamStartDate: string;
  teamEndDate: string;
  status: string;
  remark?: string;
  participant?: string[];
  checkbox?: string;
  participationType: string;
  campaignParticipationType?: string;
  pin?: number;
}

const initTeamForm: CreateTeamData = {
  teamId: null,
  teamName: '',
  campaignId: '',
  campaignName: '',
  coachId: '',
  teamLeader: '',
  teamStartDate: '',
  teamEndDate: '',
  status: '',
  remark: '',
  participant: [],
  checkbox: '',
  participationType: '',
};

interface CreateTeamProps {
  decodedData: AppTokenModel | null;
}

export const CreateTeam = (props: CreateTeamProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');

  const [masterList, setMasterList] = useState<{
    coachList: DynamicObjectInterface;
    campaignList: DynamicObjectInterface;
  }>();
  const [teamData, setTeamData] = useState<CreateTeamData>(initTeamForm);
  const [teamSize, setTeamSize] = useState(0);
  const [coachEmail, setCoachEmail] = useState<string>('');
  const [joinTeamLink, setJoinTeamLink] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showNoCampaignPopup, setShowNoCampaignPopup] = useState(false);
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [validateUser, setValidateUser] = useState(true);
  const [clickedFields, setClickedFields] = useState({
    campaignId: false,
    teamName: false,
    teamLeader: false,
    participant: [],
  });
  const [emailInvalid, setEmailInvalid] = useState<{
    teamLeader: boolean;
    [key: string]: boolean | undefined;
  }>({ teamLeader: false });
  const [requiredEmailOtp, setRequiredEmailOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const apiCall = useAPICall();

  const validateInputs = useMemo(() => {
    if (!validateUser) {
      return true;
    }
    const { teamName, participant, participationType } = teamData;
    const isAnyFieldEmpty =
      !teamName ||
      Object.keys(participant ?? {}).length !== (teamSize > 0 ? Number(teamSize - 1) : 0) ||
      Object.values(emailInvalid ?? {}).some(value => value === true);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const participantEmailsValid = Object.values(participant ?? {}).every(email => emailRegex.test(email));
    if ((!isAnyFieldEmpty && participantEmailsValid) || participationType === 'ind') {
      return false;
    }

    return true;
  }, [teamData]);

  const handleInputChange = async (name: string, value: DynamicObjectInterface) => {
    if (name.startsWith('participant_')) {
      setTeamData((prevTeamData: DynamicObjectInterface) => ({
        ...prevTeamData!,
        participant: {
          ...prevTeamData.participant,
          [name]: value,
        },
      }));
      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        participant: {
          ...prevClickedFields.participant,
          [name]: value === '',
        },
      }));
      setEmailInvalid((prevData: DynamicObjectInterface) => ({
        ...prevData!,
        [name]: value !== '' && !isValidEmail(value as string),
      }));
    } else {
      if (name === 'checkbox' && teamData?.checkbox) {
        value = '';
      }

      if (name === 'campaignId') {
        setEmailInvalid({ teamLeader: false });
        setTeamData((prevTeamData: DynamicObjectInterface) => ({
          ...prevTeamData!,
          teamLeader: '',
          participant: {},
        }));

        let campaignRecId = masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === value)?.recId;

        let result = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
        setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
      }

      if (name === 'teamLeader') {
        setEmailInvalid((prevData: DynamicObjectInterface) => ({
          ...prevData!,
          [name]: !isValidEmail(value as string),
        }));
      }

      setTeamData(prevTeamData => ({
        ...prevTeamData!,
        [name]: value,
      }));

      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        [name]: value === '',
      }));
    }
  };

  const cacheKey = 'emailOtp';
  const getEmailOtpSettings = async () => {
    try {
      const data = await getContent('email_otp', cacheKey, false);

      setRequiredEmailOtp(data);
    } catch (error) {
      LoggerService.log('Failed to retrieve content data. Using default content.');
    }
  };

  const onSave = async () => {
    const payload = {
      campaignId: teamData?.campaignId,
      campaignName: teamData?.campaignName,
      teamName: teamData?.teamName,
      coachId: props.decodedData?.userId,
      memberEmails: [
        ...Object.values(teamData?.participant ?? {}).map(email => ({ email, isTeamLead: false })),
        { email: coachEmail, isTeamLead: true },
      ],
      loginId: props.decodedData?.loginId,
      description: teamData?.remark,
      startDate: new Date().toISOString().slice(0, -1),
      pin: teamData?.pin,
    };

    let result = await apiCall(TeamService.createTeam, payload);
    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: getTranslatedMessage(t, result?.data?.message),
          theme: Theme.ERROR,
        });
      }
    } else {
      setJoinTeamLink(result?.data?.link);
      setShowConfirmation(false);
      setShowNotification(true);
    }
  };

  const teamDetailsBtnConfig = [
    {
      title: t('dashboard.create-team.button.continue'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: validateInputs,
      onClick: () => {
        if (teamData.participationType === 'ind') {
          navigate(`/join-team/register?tmI=ind&cmI=${teamData?.campaignRecId}`);
        } else {
          setShowConfirmation(true);
        }
      },
    },
  ];

  const handleJoinTeam = () => {
    const encodedValue = new URLSearchParams(joinTeamLink.split('?')[1]).get('t');

    if (encodedValue) {
      const decoded = atob(encodedValue);
      const values = decoded.split(',').map(Number);
      window.location.href = ` join-team/register?cmI=${values[0]}&tmI=${values[1]}&tmL=${values[2]}`;
    } else {
      window.location.href = joinTeamLink;
    }

    setShowNotification(false);
  };

  const confirmationPrompt = (
    <>
      {showConfirmation && (
        <Modal
          primaryButtonLabel={''}
          headline={t('dashboard.create-team.modal.confirmation.teamConfirmation')}
          children={
            <>
              <div
                style={{
                  fontSize: '16px',
                  fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
                  lineHeight: '24px',
                  margin: '10px 0',
                }}
              >
                {t('dashboard.create-team.modal.confirmation.confirmation')}
              </div>
              <div style={{ display: 'flex', margin: '32px 0' }}>
                <Checkbox
                  key={'checkbox'}
                  className={styles['checkbox']}
                  checked={teamData.checkbox === 'on'}
                  theme={Theme.BLACK}
                  text={
                    <p
                      id="agree-checkbox"
                      dangerouslySetInnerHTML={{
                        __html: t('dashboard.create-team.modal.confirmation.termsLine1').replace(
                          '{terms-conditions}',
                          `<a href="/terms-and-conditions?cid=${teamData?.campaignId}" target="_blank">${t(
                            'securityPrivacy.terms-condition',
                          )}</a>`,
                        ),
                      }}
                    />
                  }
                  onChange={e => handleInputChange('checkbox', e.target.value)}
                />
                <>
                  {/* {t('admin.teamCreate.termsLine1')} */}
                  <br />
                  {/* {t('admin.teamCreate.termsLine2')} */}
                </>
              </div>
            </>
          }
          closeIcon={false}
          containerWidth={'max-content'}
          textAlign={TextAlign.LEFT}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.confirmation.button.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onSave();
              }}
              size={ButtonSize.SMALL}
              theme={Theme.GREEN}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
              disabled={!teamData?.checkbox}
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.create-team.modal.confirmation.button.back')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowConfirmation(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.GREEN}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const noCampaignPrompt = (
    <>
      {showNoCampaignPopup && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={isDesktopView ? '60%' : '80%'}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.noCampaign.button.backToHomepage')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                navigate(`/`);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={undefined}
        >
          <div>
            <div>
              <img src={JoinTeamImage} alt={`JoinTeamImage`} height={isDesktopView ? '400px' : '200px'} />
            </div>
            <div className={styles['sub-header']}>{t('dashboard.create-team.modal.noCampaign.description')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const notificationPrompt = (
    <>
      {showNotification && (
        <Modal
          primaryButtonLabel={''}
          containerWidth={'max-content'}
          headline={''}
          closeIcon={true}
          onClose={() => {
            navigate(`/`);
          }}
          textAlign={TextAlign.CENTER}
          primaryButton={
            <Button
              title={t('dashboard.create-team.modal.notification.button.joinTeam')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              onClick={() => {
                handleJoinTeam();
                // setShowNotification(false);
                // window.location.href = joinTeamLink;
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.create-team.modal.notification.button.share')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              onClick={() => {
                setShowShareDrawer(true);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              icon={{ iconName: 'share-alt', iconStyle: IconStyle.SOLID }}
              iconPosition={ButtonIconPosition.LEFT}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <div className={styles['modal-container']}>
            <div className={styles['modal-img']}>
              <img src={CreateTeamImage} alt={`createTeamImage`} width="295px" />
            </div>
            <div className={styles['modal-headline']}>{t('dashboard.create-team.modal.notification.teamSetup')}</div>
            <div className={styles['modal-desc']}>{t('dashboard.create-team.modal.notification.sentEmail')}</div>
          </div>
        </Modal>
      )}
    </>
  );

  const participantInput = Array.from({ length: teamSize - 1 }, (_, index) => {
    const plusOne = index;
    return (
      <div className={styles['participant-email-container']}>
        <p className={styles['input-label']}>
          {t('dashboard.create-team.input.participant')} {plusOne + 1}
          <span className={styles['label-red']}>*</span>
        </p>
        <Input
          placeholder={t('dashboard.create-team.input.placeholder.email')}
          key={`${teamData?.campaignId}_participant_${index}`}
          type={InputTypes.TEXT}
          name={`participant_${index + 1}`}
          mlpVersion={MLPVersion.ONE}
          onChange={e => {
            handleInputChange(`participant_${index}`, e.target.value);
          }}
          status={emailInvalid?.[`participant_${index}`] ? InputValidations.INVALID : InputValidations.DEFAULT}
          message={
            emailInvalid?.[`participant_${index}`] ? `${t('dashboard.create-team.message.invalidEmailFormat')}` : ''
          }
        />
      </div>
    );
  });

  const sharePrompt = (
    <>
      {showShareDrawer && (
        <ShareDrawer
          open={!!joinTeamLink}
          onClose={() => setShowShareDrawer(false)}
          title={t('dashboard.create-team.button.shareVia')}
          programMessage={t('dashboard.create-team.message.invitedToJoinCampaign')
            .replace('{campaignName}', teamData?.campaignName)
            .replace('{join-team-link}', joinTeamLink)}
          programUrl={joinTeamLink}
          channels={process.env.REACT_APP_COUNTRY === 'PH' ? ['FaWhatsapp', 'FaViber','FaLink'] : ['FaWhatsapp', 'FaLink']}
          programTitle=""
        />
      )}
    </>
  );

  useEffect(() => {
    (async () => {
      let apiList = [
        apiCall(userService.getUserDetails, `${props.decodedData?.userId}`),
        apiCall(CampaignService.getCampaigns),
      ];
      let res = await Promise.all(apiList);

      let masterList = {
        coachList: [
          // ...(res?.[0]?.data?.data || []).map((d: DynamicObjectInterface) => ({
          //   ...d,
          //   id: d.coachID,
          //   title: `${d.coachID} - ${d.name}`,
          // })),
        ],
        campaignList: (
          res?.[1]?.data?.data.filter(
            (i: DynamicObjectInterface) =>
              new Date(i.registerEnd).getTime() > new Date().getTime() &&
              i.status !== 'DELETED' &&
              i.status !== 'CLOSED',
          ) || []
        ).map((d: DynamicObjectInterface) => ({
          ...d,
          value: d.campaignId,
          title: `${d.campaignId} - ${d.campaignName}`,
          campaignName: d.campaignName,
        })),
      };

      setCoachEmail(res?.[0]?.data?.data?.email ? res?.[0]?.data?.data?.email : '');
      setEmailVerified(res?.[0]?.data?.data?.emailVerified === 1 ? true : false);
      setMasterList(masterList);
      // After getting the master list, use the campaignRecId to validate if the user can join the team
      if (masterList?.campaignList?.length <= 0) {
        setShowNoCampaignPopup(true);
      }
    })();

    getEmailOtpSettings();

    const timeout = setTimeout(() => {
      trackPage(PAGES.createTeam);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const initializeTeamData = async () => {
      if (masterList?.campaignList?.length > 0) {
        const firstCampaign = masterList?.campaignList[0];
        const participationType = JSON.parse(firstCampaign.participationType)?.includes('team') ? 'team' : 'ind';

        setTeamData(prevTeamData => ({
          ...prevTeamData,
          campaignId: firstCampaign.value,
          campaignName: firstCampaign.campaignName,
          campaignRecId: firstCampaign.recId,
          campaignParticipationType: firstCampaign.participationType,
          participationType,
        }));
        setTeamSize(firstCampaign?.teamSize);
        await validUserJoinTeam(firstCampaign.recId, participationType);
      }
    };

    initializeTeamData();
  }, [masterList]);

  const validUserJoinTeam = async (teamRecId: number, participationType: string) => {
    const payload = {
      campaignRecId: teamRecId,
      loginId: props.decodedData?.loginId,
      partyId: props.decodedData?.partyId,
    };
    let validToCreate = await apiCall(ParticipantService.validateJoinTeam, payload);

    setValidateUser(
      participationType === 'ind'
        ? validToCreate?.data?.data?.canEnrollParticipant
        : validToCreate?.data?.data?.canCreateTeam,
    );
    setTeamData(prevTeamData => ({
      ...prevTeamData,
      pin: validToCreate?.data?.data?.pin,
    }));
  };

  return (
    <div className={styles['edit-team']}>
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>{t('dashboard.create-team.label.title')}</h2>
          {isDesktopView && (
            <>
              <div className={styles['actions-container']}>
                {teamDetailsBtnConfig.map(btn => (
                  <Button key={btn.title} theme={Theme.GREEN} size={ButtonSize.SMALL} {...btn} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className={styles['create-team-form-container']}>
          <div className={styles['dropdown-container']}>
            {/* <div className={styles['dropdown-label']}>
              {t('admin.teams.campaign')} <span className={styles['label-red']}>*</span>
            </div> */}
            <div className={styles['dropdown-test']}>
              <DropDown
                name="campaignId"
                disabledFilter={false}
                mlpVersion={MLPVersion.ONE}
                value={
                  masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === teamData?.campaignId)?.title
                }
                dropdownLabel={t('dashboard.create-team.input.campaign')}
                data={masterList?.campaignList}
                configData={{
                  label: 'title',
                  onSelect: async (selectedOption: DropDownData) => {
                    handleInputChange('campaignId', selectedOption.value);
                    handleInputChange('campaignRecId', selectedOption.recId);
                    await validUserJoinTeam(Number(selectedOption.recId), teamData.participationType);
                    handleInputChange('campaignName', selectedOption.campaignName);
                    handleInputChange('campaignParticipationType', selectedOption.participationType);
                    handleInputChange(
                      'participationType',
                      typeof selectedOption.participationType === 'string' &&
                        JSON.parse(selectedOption.participationType)?.includes('team')
                        ? 'team'
                        : 'ind',
                    );
                  },
                }}
                readOnly={false}
              />
              {teamData?.pin === undefined ? (
                <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div className={styles['error-text']}>{t('dashboard.create-team.message.unexpectedError')}</div>
                </div>
              ) : (
                !validateUser && (
                  <div className={styles['validate-user-container']}>
                    <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                    <div className={styles['error-text']}>{t('dashboard.create-team.message.notFulfilCondition')}</div>
                  </div>
                )
              )}
            </div>
          </div>

          {teamData?.campaignParticipationType &&
            teamData?.campaignParticipationType.includes('team') &&
            teamData?.campaignParticipationType.includes('ind') && (
              <div className={styles['input-container']}>
                <div className={styles['label-text']}>
                  <div>
                    {t('dashboard.create-team.input.joinAs')}
                    <span className={styles['label-red']}> *</span>
                  </div>
                  <div>
                    <Tooltip
                      message={t('dashboard.create-team.input.tooltip.joinAs')}
                      preference={TooltipPreference.BOTTOM_CENTER}
                      className={styles['input-tool-tip']}
                      iconName="circle-exclamation"
                    />
                  </div>
                </div>
                <div className={styles['radio-button-container']}>
                  <RadioButton
                    label={t('dashboard.create-team.input.team')}
                    checked={teamData?.participationType === 'team'}
                    onChange={async () => {
                      teamData.campaignRecId && (await validUserJoinTeam(teamData.campaignRecId, 'team'));
                      handleInputChange('participationType', 'team');
                    }}
                  />

                  <RadioButton
                    label={t('dashboard.create-team.input.individual')}
                    checked={teamData?.participationType === 'ind'}
                    onChange={async () => {
                      teamData.campaignRecId && (await validUserJoinTeam(teamData.campaignRecId, 'ind'));
                      handleInputChange('participationType', 'ind');
                    }}
                  />
                </div>
              </div>
            )}
        </div>

        {teamData?.participationType === 'team' && (
          <div className={styles['create-team-form-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'teamName'}
              labelClassName={styles['team-name-label']}
              label={t('dashboard.create-team.input.teamName')}
              value={teamData?.teamName}
              required
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('teamName', e.target.value);
              }}
              placeholder={t('dashboard.create-team.input.placeholder.teamName')}
              status={clickedFields.teamName ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={clickedFields.teamName ? `${t('admin.teamCreate.teamNameRequired')}` : ''}
            />
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'teamLeader'}
              label={t('dashboard.create-team.input.teamLeader')}
              labelClassName={styles['team-leader-label']}
              value={`${props.decodedData?.loginId} ${props.decodedData?.name}`}
              mlpVersion={MLPVersion.ONE}
              disabled={true}
            />
          </div>
        )}
        {/* <div className={styles['create-team-form-container']}>
          <Input
            labelClassName={styles['remarks-test']}
            key={id}
            type={InputTypes.TEXT}
            name={'remark'}
            label={`${t('dashboard.create-team.input.remarks')}`}
            value={teamData?.remark}
            placeholder="Remarks"
            mlpVersion={MLPVersion.ONE}
            // labelPosition={LabelPosition.LEFT}
            onChange={e => {
              handleInputChange('remark', e.target.value);
            }}
          />
        </div> */}
        {teamData?.participationType === 'team' && (
          <div className={styles['card-send-email-invitation']}>
            <div
              className={styles['card-send-email-invitation-title']}
            >{`${t('dashboard.create-team.label.sendEmailInvitation')}`}</div>
            <div className={styles['card-send-email-invitation-desc']}>
              {`${t('dashboard.create-team.label.emailInvitationDescription')?.replace('{teamSize}', (teamSize - 1 || '').toString())}`}
            </div>
            <div className={styles['card-send-email-invitation-input-container']}>
              <div className={styles['participant-email-container']}>
                <p className={styles['input-label']}>
                  {t('dashboard.create-team.input.teamLeader')} <span style={{ color: 'red' }}> *</span> &nbsp;
                  {requiredEmailOtp && (
                    <Tooltip
                      message={t('dashboard.create-team.input.tooltip.email')}
                      className={styles['input-tool-tip']}
                    />
                  )}
                </p>

                <Input
                  key={id}
                  type={InputTypes.TEXT}
                  name={'teamLeader'}
                  value={`${coachEmail} `}
                  placeholder={t('dashboard.create-team.input.placeholder.teamLeader')}
                  mlpVersion={MLPVersion.ONE}
                  disabled={true}
                  onChange={e => {
                    handleInputChange('teamLeader', e.target.value);
                  }}
                  status={!coachEmail ? InputValidations.WARNING : InputValidations.DEFAULT}
                  message={
                    requiredEmailOtp && emailVerified ? (
                      <div className={styles['participant-section-input-message']}>
                        <FAIcon
                          iconName={'circle-check'}
                          theme={Theme.SUCCESS}
                          iconStyle={IconStyle.SOLID}
                          className={styles['modal-reward-delete']}
                        />
                        <div>{t('dashboard.create-team.message.emailVerified')}</div>
                      </div>
                    ) : !coachEmail ? (
                      t('dashboard.create-team.message.teamLeaderEmailMissing')
                    ) : (
                      ''
                    )
                  }
                />
              </div>
              {/* {!coachEmail && (
                <div className={styles['validate-user-container']}>
                  <FAIcon theme={Theme.WARNING} iconName="exclamation-circle" iconStyle={IconStyle.SOLID} />
                  <div className={styles['error-text']}>
                    {'Please fill up your email address at Profile page before proceeding to create team!'}
                  </div>
                </div>
              )} */}
              {participantInput}
            </div>
          </div>
        )}
        <p
          className={styles['input-message-footnote']}
          dangerouslySetInnerHTML={{
            __html: t('dashboard.create-team.message.joinTeamFootnote').replace(
              '{one-month}',
              `<b>${t('dashboard.create-team.message.oneMonth')}</b>`,
            ),
          }}
        />
        {/* <div className={styles['input-message']}>{`*${t('admin.dashboard.joinTeamFootnote')}`}</div> */}
        {!isDesktopView && (
          <>
            <div className={styles['actions-container-mobile']}>
              {teamDetailsBtnConfig.map(btn => (
                <Button
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  theme={Theme.GREEN}
                  key={btn.title}
                  size={ButtonSize.SMALL}
                  {...btn}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {confirmationPrompt}
      {notificationPrompt}
      {noCampaignPrompt}
      {sharePrompt}
    </div>
  );
};
